import { useState, useMemo } from 'react';

interface DatePickerProps {
  selected: string | null;
  onChange: (date: string) => void;
  locale?: string;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function formatDate(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function isPastDate(year: number, month: number, day: number): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(year, month, day);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate < today;
}

function isWeekend(year: number, month: number, day: number): boolean {
  const d = new Date(year, month, day).getDay();
  return d === 0 || d === 6;
}

function getMonthName(year: number, month: number, locale: string): string {
  const date = new Date(year, month, 1);
  return date.toLocaleDateString(locale, { month: 'long' });
}

function getWeekdayAbbreviations(locale: string): string[] {
  const base = new Date(2024, 0, 1);
  const weekdays: string[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(base);
    day.setDate(base.getDate() + i);
    weekdays.push(day.toLocaleDateString(locale, { weekday: 'short' }).slice(0, 2));
  }
  return weekdays;
}

export default function DatePicker({ selected, onChange, locale = 'de' }: DatePickerProps) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const weekdays = useMemo(() => getWeekdayAbbreviations(locale), [locale]);

  const days = useMemo(() => {
    const daysInMonth = getDaysInMonth(viewYear, viewMonth);
    const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
    const result: (number | null)[] = [];

    for (let i = 0; i < firstDay; i++) {
      result.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      result.push(d);
    }
    return result;
  }, [viewYear, viewMonth]);

  const goToPrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  const handleSelect = (day: number) => {
    if (isPastDate(viewYear, viewMonth, day)) return;
    onChange(formatDate(viewYear, viewMonth, day));
  };

  const isSelected = (day: number) => {
    return selected === formatDate(viewYear, viewMonth, day);
  };

  const isToday = (day: number) => {
    const t = new Date();
    return viewYear === t.getFullYear() && viewMonth === t.getMonth() && day === t.getDate();
  };

  return (
    <div className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <button
          onClick={goToPrevMonth}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-foreground-600"></i>
        </button>
        <span className="font-heading text-lg font-semibold text-foreground-800">
          {getMonthName(viewYear, viewMonth, locale)} {viewYear}
        </span>
        <button
          onClick={goToNextMonth}
          className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-background-100 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-foreground-600"></i>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {weekdays.map((wd) => (
          <div key={wd} className="text-center text-xs font-label font-medium text-foreground-400 py-1">
            {wd}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="aspect-square"></div>;
          }
          const past = isPastDate(viewYear, viewMonth, day);
          const sel = isSelected(day);
          const todayFlag = isToday(day);
          const wknd = isWeekend(viewYear, viewMonth, day);

          return (
            <button
              key={day}
              onClick={() => handleSelect(day)}
              disabled={past}
              className={`aspect-square flex items-center justify-center rounded-full text-sm font-label transition-all duration-200 ${
                sel
                  ? 'bg-primary-500 text-white font-semibold'
                  : past
                  ? 'text-foreground-200 cursor-not-allowed'
                  : todayFlag
                  ? 'bg-primary-100 text-primary-700 font-semibold hover:bg-primary-200 cursor-pointer'
                  : wknd
                  ? 'text-foreground-300 hover:bg-background-100 cursor-pointer'
                  : 'text-foreground-700 hover:bg-background-100 cursor-pointer'
              }`}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );
}